import PasswordPopper from "@mirage/components/passwordPopper/PasswordPopper";
import ErrorSharp from "@mui/icons-material/ErrorSharp";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Alert, AlertColor, Box, Button, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import Stack from "@mui/material/Stack";
import { ResultBoardStateSetter } from "@shared/mirage/core/contracts/ResultBoard";
import { SetNewPasswordRequest, SetNewPasswordResult, ValidateForgottenPasswordTokenRequest, ValidateForgottenPasswordTokenResult } from "@shared/mirage/core/contracts/User";
import validators, { IPasswordValidatorResult, ITwoPasswordsValidatorResult } from "@shared/mirage/validators";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IResetPasswordService } from "@shared/mirage/services/IResetPasswordService";
import { ChangePasswordFields, IChangePasswordInputData } from "../changePassword/ChangePasswordBoilerplate";

interface ResetPasswordBoilerplateProps {
    service: IResetPasswordService;
    backButtonRoute: string;
    setResultBoard: ResultBoardStateSetter;
}

const ResetPasswordBoilerplate = (props: ResetPasswordBoilerplateProps) => {
    const [newPassword, setNewPassword] = useState({ value: "", type: "password", showPassword: false, validationResult: { passed: true } } as IChangePasswordInputData);
    const [passwordPopperAnchorElement, setPasswordPopperAnchorElement] = useState<null | HTMLElement>(null);
    const [passwordPopperVisibility, setPasswordPopperVisibility] = useState(false);
    const [newPasswordConfirm, setNewPasswordConfirm] = useState({
        value: "",
        type: "password",
        showPassword: false,
        validationResult: { passed: true },
    } as IChangePasswordInputData);
    const [alert, setAlert] = useState({ show: false, severity: "" as AlertColor, text: "" });
    const navigate = useNavigate();
    const [token, setToken] = useState({ contents: "", isValid: false });

    useEffect(() => {
        const validateToken = async () => {
            const search = location.search;
            const token = search?.slice(3);

            if (token.length) {
                const request: ValidateForgottenPasswordTokenRequest = {
                    token,
                };

                const requestResult: ValidateForgottenPasswordTokenResult = await props.service.validateForgottenPasswordToken(request);
                if (requestResult.errorMsg) {
                    props.setResultBoard({ show: true, message: requestResult.errorMsg, success: false, isLoading: false });
                } else if (requestResult.id) {
                    setToken({ contents: token, isValid: true });
                }
            }
        };

        validateToken();
    }, []);

    const getErrorMessage = (error: boolean | undefined, errorMessage: string | undefined) => {
        return !error ? (
            <span style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                <ErrorSharp sx={{ width: "0.7em", height: "0.7em" }}></ErrorSharp>
                <span>{errorMessage}</span>
            </span>
        ) : null;
    };

    const getPassFieldsEndAdornment = (fieldRef: ChangePasswordFields) => {
        const showPassword = fieldRef === "newPassword" ? newPassword.showPassword : newPasswordConfirm.showPassword;
        return (
            <InputAdornment position="end">
                <IconButton onClick={() => showPasswordHandler(fieldRef)} onMouseDown={handleMouseDownPassword}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
            </InputAdornment>
        );
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const showPasswordHandler = (reference: ChangePasswordFields, hidePassword?: true) => {
        switch (reference) {
            case "newPassword":
                setNewPassword((previousState) => {
                    return {
                        ...previousState,
                        showPassword: hidePassword ? false : !previousState.showPassword,
                    };
                });
                break;
            case "confirmNewPassword":
                setNewPasswordConfirm((previousState) => {
                    return {
                        ...previousState,
                        showPassword: hidePassword ? false : !previousState.showPassword,
                    };
                });
                break;
        }
    };

    const handleInputChange = (value: string, reference: ChangePasswordFields) => {
        switch (reference) {
            case "newPassword":
                setNewPassword((previousState) => {
                    const passwordValidationResult: IPasswordValidatorResult = validators.PasswordValidator(value);
                    return {
                        ...previousState,
                        value: value,
                        validationResult: passwordValidationResult,
                    };
                });
                break;
            case "confirmNewPassword":
                setNewPasswordConfirm((previousState) => {
                    const passwordComplianceValidationResult: ITwoPasswordsValidatorResult = validators.CompareTwoPasswords(newPassword.value, value);
                    return {
                        ...previousState,
                        value: value,
                        validationResult: passwordComplianceValidationResult,
                    };
                });
                break;
            default:
                throw Error(`Bad reference for Input Change - ${reference}`);
        }
    };

    const handleInputBlur = (reference: ChangePasswordFields) => {
        const validationResult = validateInput(reference);
        handleInputValidation(reference, validationResult);
    };

    const validateInput = (reference: ChangePasswordFields) => {
        switch (reference) {
            case "newPassword":
                return validators.PasswordValidator(newPassword.value);
            case "confirmNewPassword":
                return validators.CompareTwoPasswords(newPassword.value, newPasswordConfirm.value);
            default:
                throw Error(`Bad reference for validation - ${reference}`);
        }
    };

    const handleInputValidation = (reference: ChangePasswordFields, validationResult: { passed?: boolean; error?: string }) => {
        switch (reference) {
            case "newPassword":
                setNewPassword((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "confirmNewPassword":
                setNewPasswordConfirm((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            default:
                throw Error(`Bad reference for Input Validation - ${reference}`);
        }
    };

    const handleConfirm = async () => {
        const newPasswordValidation = validateInput("newPassword");
        const confirmNewPasswordValidation = validateInput("confirmNewPassword");

        if (newPasswordValidation.passed && confirmNewPasswordValidation.passed && token.isValid) {
            const resetPasswordRequest: SetNewPasswordRequest = {
                newPassword: newPassword.value,
                confirmNewPassword: newPasswordConfirm.value,
                token: token.contents,
            };

            const resetPasswordRes: SetNewPasswordResult = await props.service.setNewPassword(resetPasswordRequest);

            if (resetPasswordRes.errorMsg) {
                setAlert((prevState) => ({ ...prevState, severity: "error", show: true, text: resetPasswordRes.errorMsg }));
            } else {
                props.setResultBoard({ show: true, message: "Your password has been changed successfully.", success: true, isLoading: false });
            }
        } else {
            handleInputValidation("newPassword", newPasswordValidation);
            handleInputValidation("confirmNewPassword", confirmNewPasswordValidation);
        }
    };

    const getButtons = () => {
        return (
            <>
                <Grid size={{ xs: 3 }} textAlign="center">
                    <Button onClick={() => navigate(props.backButtonRoute)}>Cancel</Button>
                </Grid>
                <Grid size={{ xs: 3 }} textAlign="center">
                    <Button variant="contained" disabled={!token.isValid} onClick={handleConfirm}>
                        Confirm
                    </Button>
                </Grid>
            </>
        );
    };

    const closeAlert = () => {
        setAlert((previousState) => {
            return {
                ...previousState,
                show: false,
            };
        });
    };

    const getResetPasswordBoard = () => {
        return (
            <>
                <Stack spacing={4} justifyContent={"center"} alignItems={"center"}>
                    {passwordPopperVisibility && passwordPopperAnchorElement && newPassword.validationResult.conditions ? (
                        <PasswordPopper anchorElement={passwordPopperAnchorElement} conditions={newPassword.validationResult.conditions}></PasswordPopper>
                    ) : (
                        ""
                    )}
                    <TextField
                        sx={{ width: "60%" }}
                        required={true}
                        id="resetPassword-newPassword"
                        error={!newPassword.validationResult.passed}
                        defaultValue={newPassword.value}
                        type={newPassword.showPassword ? "text" : newPassword.type}
                        InputProps={{ endAdornment: getPassFieldsEndAdornment("newPassword") }}
                        onChange={(e) => handleInputChange(e.target.value, "newPassword")}
                        onFocus={(e) => {
                            handleInputBlur("newPassword");
                            const inputContainer = e.currentTarget.parentElement;

                            if (!passwordPopperAnchorElement) {
                                setPasswordPopperAnchorElement(inputContainer);
                            }
                            if (!passwordPopperVisibility) {
                                setPasswordPopperVisibility(true);
                            }
                        }}
                        onBlur={() => setPasswordPopperVisibility(false)}
                        label="Password"
                        variant="outlined"
                    />
                    <TextField
                        sx={{ width: "60%" }}
                        required={true}
                        id="resetPassword-newPasswordConfirm"
                        error={!newPasswordConfirm.validationResult.passed}
                        type={newPasswordConfirm.showPassword ? "text" : newPasswordConfirm.type}
                        helperText={getErrorMessage(newPasswordConfirm.validationResult.passed, newPasswordConfirm.validationResult.error)}
                        InputProps={{ endAdornment: getPassFieldsEndAdornment("confirmNewPassword") }}
                        onChange={(e) => handleInputChange(e.target.value, "confirmNewPassword")}
                        onBlur={() => handleInputBlur("confirmNewPassword")}
                        label="Confirm Password"
                        variant="outlined"
                    />
                </Stack>

                <Grid container sx={{ marginTop: "2rem" }} justifyContent="center">
                    {getButtons()}
                </Grid>
            </>
        );
    };

    return (
        <>
            {getResetPasswordBoard()}
            <Box position="absolute" bottom="20px" left="20px" width="40%">
                {alert.show ? (
                    <Alert severity={alert.severity} onClose={closeAlert}>
                        {alert.text}
                    </Alert>
                ) : null}
            </Box>
        </>
    );
};

export default ResetPasswordBoilerplate;
